// @flow
import * as React from 'react';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import useHighlightCable from 'esm-web-pro/containers/pages/network/objects/cable/useHighlightCable';
import useHighlightFiber from 'esm-web-pro/containers/pages/network/objects/fiber/useHighlightFiber';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import type { LoadingCallback } from 'esm-web-pro/containers/map/editor/BaseEditor';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';

type Props = {
    cable: Object,
    editor: BaseEditor,
    children: (highlightFiber: Function, highlightCable: Function, handleView: Function) => React.Node,
    leaflet: LeafletContext,
};

export const numberPointCallback = (containerPoint: { coordinates: { coordinates: [number, number] },
    sequence: number }): LoadingCallback => {
    const pointCoords = containerPoint.coordinates.coordinates;
    let number = containerPoint.sequence;
    if (containerPoint.type === 'pop' && containerPoint.alias) {
        number = containerPoint.alias;
    }
    return UseHighlightPoint(pointCoords, number);
};

export const numberPointsCallbacksCable = (cable: Object): LoadingCallback[] => {
    const {
        start_container_point: startContainerPoint, end_container_point: endContainerPoint,
    } = cable;

    if (!startContainerPoint && !endContainerPoint) return [];
    return [numberPointCallback(startContainerPoint), numberPointCallback(endContainerPoint)];
};

const MapCable = (props: Props) => {
    const {
        cable, editor, children, leaflet: { map },
    } = props;
    const { cable_path: cablePath } = cable;
    const cableCoordinates = cablePath.path.map((c) => [c[1], c[0]]);

    const highlightCable = useHighlightCable(cableCoordinates);
    const [highlightFiber, highlightCleanUp] = useHighlightFiber();

    const handleView = () => {
        editor.unregisterAllLoadingCallback();
        editor.registerMultipleLoadingCallbacks(numberPointsCallbacksCable(cable));
        editor.registerLoadingCallback(highlightCable);
        editor.reloadFeatures();
        if (map) {
            // Go to the middle path of the cable
            map.highlightedGeometry = {
                bounds: cableCoordinates, center: null,
            };
            map.panInsideBounds(cableCoordinates);
        }
    };

    React.useEffect(() => {
        handleView();
        return () => {
            editor.unregisterAllLoadingCallback();
            highlightCleanUp();
        };
    }, []);

    return children(highlightFiber, highlightCable, handleView);
};

export default withLeaflet(MapCable);
